// src/components/cards/TodayTasksCard.jsx
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import CircularProgress from "../../../../components/charts/CircularProgress";
import { useTodos } from "../../../../hooks/useTodos";
import { useQueryClient } from "@tanstack/react-query";
import { SparklesIcon } from "lucide-react";

function yyyymmdd(date) {
    return new Date(date).toLocaleDateString("sv-SE"); // YYYY-MM-DD
}

export default function TodayTasksCard({ userId, onClick }) {
    const qc = useQueryClient();
    const { data: all = [], isLoading, isError } = useTodos();
    const today = yyyymmdd(new Date());

    // 집계 기준 세트(오늘 날짜 필드가 있으면 '오늘 것 전체', 없으면 전체)
    const hasDateField = all.some(t => t?.due_date || t?.date);
    const todayAll = hasDateField
        ? all.filter(t => {
            const d = t?.due_date || t?.date;
            if (!d) return false;
            const ymd = String(d).slice(0, 10);
            return ymd === today;
        })
        : all;

    // 화면 표시용 (미리보기 최대 3개)
    const visible = todayAll.slice(0, 3);

    // 계산은 전체 데이터로
    const completed = todayAll.filter(t => t.done).length;
    const total = todayAll.length;
    const percentInt = total ? Math.round((completed / total) * 100) : 0;
    const doneText = `${completed}/${total}개 실천 완료`;

    return (
        <div
            onClick={onClick}
            className="
                px-6 py-5 md:py-4
                bg-white rounded-2xl
                border border-gray-200
                grid grid-cols-[1fr_auto] gap-4
                items-start 
            "
        >
            {/* 리스트: 미리보기 최대 3개 */}
            <div className="space-y-3">
                <ul className="space-y-2 md:space-y-3 text-base md:text-lg ">
                    {visible.map((t) => (
                        <li key={t.id}>
                            <div
                                role="button"
                                aria-pressed={t.done}
                                className="w-full flex items-center gap-5 text-left rounded-lg select-none"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClick?.();
                                }}
                            >
                                {t.done ? (
                                    <CheckCircleIcon className="w-7 h-7 text-[#5F9BFF]" aria-hidden />
                                ) : (
                                    <CheckCircleIcon className="w-7 h-7 text-gray-300" aria-hidden />
                                )}
                                <span className={t.done ? "font-bold line-through text-gray-400" : "font-bold"}>
                                    {t.title}
                                </span>
                            </div>
                        </li>
                    ))}
                    {visible.length === 0 && (
                        <li className="h-full flex flex-col justify-center">
                            <div className="flex items-center gap-2 text-gray-500 mb-1">
                                <span className="font-bold text-gray-600">예정된 일정이 없습니다.</span>
                            </div>
                            <p className="text-sm text-gray-400">
                                일정을 등록해주십시오.
                            </p>
                        </li>
                    )}
                </ul>
            </div>

            {/* 원형 그래프: 전체(todayAll) 기준 */}
            <div className="self-center">
                <CircularProgress
                    value={percentInt}
                    label={`${percentInt}`}
                    className="w-[75px] h-[75px] md:w-24 md:h-24"
                    type="today-tasks"
                    textsize="text-2xl md:text-3xl"
                />
                <p className="text-xs md:text-sm text-gray-500">{doneText}</p>
            </div>
        </div>
    );
}
